import React, { useState, useEffect } from "react";
import { Package, X, Plus, MoreVertical, Edit, Trash2, Layers } from "lucide-react";
import { Product } from "../../../types/product";
import { formatPrice } from "../../../utils/utility";
import { formatDate } from "../../../utils/date";
import { useNavigate } from "react-router-dom";

interface ProductListProps {
  products: Product[];
  isLoading: boolean;
  error: Error | null;
  searchQuery: string;
  onClearSearch: () => void;
  onDeleteProduct: (id: string) => void;
}

// Best available image: main product image, else the first variant that has one.
function getProductImage(product: Product): string | null {
  if (product.images && product.images.length > 0) return product.images[0];
  if (product.hasVariants && product.variants) {
    const v = product.variants.find((x) => x.images && x.images.length > 0);
    if (v?.images?.length) return v.images[0];
  }
  return null;
}

// Price shown in the list. Simple products use their own mrp; variant products
// show the range across variants (their base mrp is often unset), or a single
// value when every variant is priced the same.
function priceLabel(product: Product): string {
  if (product.hasVariants && product.variants && product.variants.length) {
    const prices = product.variants.map((v) => v.mrp).filter((n) => typeof n === "number" && n > 0);
    if (prices.length) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      return min === max ? formatPrice(min) : `${formatPrice(min)} – ${formatPrice(max)}`;
    }
  }
  return formatPrice(product.mrp || 0);
}

// A single discount badge only makes sense for a single price. Variant products
// can discount each variant differently, so we show the price range instead.
function simpleDiscount(product: Product): number | null {
  if (product.hasVariants && product.variants && product.variants.length) return null;
  return product.discountPercent && product.discountPercent > 0 ? product.discountPercent : null;
}

const ProductThumb: React.FC<{ product: Product; className?: string }> = ({ product, className = "w-11 h-11" }) => {
  const [err, setErr] = useState(false);
  const src = getProductImage(product);
  return (
    <div className={`${className} rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center`}>
      {src && !err ? (
        <img src={src} alt={product.title} className="w-full h-full object-cover" onError={() => setErr(true)} />
      ) : (
        <Package className="w-5 h-5 text-slate-300" />
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ stock?: number }> = ({ stock }) => {
  const base = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium";
  if (!stock || stock === 0) {
    return <span className={`${base} bg-rose-50 text-rose-700`}><span className="w-1.5 h-1.5 rounded-full bg-rose-500" />Out of stock</span>;
  }
  if (stock < 10) {
    return <span className={`${base} bg-amber-50 text-amber-700`}><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Low stock</span>;
  }
  return <span className={`${base} bg-emerald-50 text-emerald-700`}><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />In stock</span>;
};

export const ProductList: React.FC<ProductListProps> = ({
  products,
  isLoading,
  error,
  searchQuery,
  onClearSearch,
  onDeleteProduct,
}) => {
  const navigate = useNavigate();
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (openDropdownId && !target.closest(".dropdown-container")) setOpenDropdownId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  const editProduct = (id: string) => {
    navigate(`../add-product?id=${id}`);
    setOpenDropdownId(null);
  };
  const deleteProduct = (id: string) => {
    onDeleteProduct(id);
    setOpenDropdownId(null);
  };

  const RowActions: React.FC<{ id: string }> = ({ id }) => (
    <div className="relative dropdown-container inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpenDropdownId((prev) => (prev === id ? null : id));
        }}
        className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
        title="Actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {openDropdownId === id && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg z-20 min-w-[140px] overflow-hidden">
          <button
            onClick={(e) => { e.stopPropagation(); editProduct(id); }}
            className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <Edit className="w-4 h-4" /> Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); deleteProduct(id); }}
            className="w-full px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      )}
    </div>
  );

  // --- Loading -------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="border-t border-slate-200 divide-y divide-slate-100">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-4 animate-pulse">
            <div className="w-11 h-11 rounded-lg bg-slate-200" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 bg-slate-200 rounded w-1/3" />
              <div className="h-3 bg-slate-100 rounded w-1/5" />
            </div>
            <div className="h-3.5 bg-slate-200 rounded w-16 hidden sm:block" />
            <div className="h-3.5 bg-slate-200 rounded w-14 hidden sm:block" />
            <div className="h-6 bg-slate-200 rounded-full w-20" />
          </div>
        ))}
      </div>
    );
  }

  // --- Error ---------------------------------------------------------------
  if (error) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-rose-500" />
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">Error loading products</h3>
          <p className="text-slate-500">{error.message}</p>
        </div>
      </div>
    );
  }

  // --- Empty ---------------------------------------------------------------
  if (products.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <Package className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1.5">
          {searchQuery ? "No products found" : "No products yet"}
        </h3>
        <p className="text-slate-500 mb-6 max-w-sm mx-auto text-sm">
          {searchQuery
            ? "We couldn't find any products matching your search. Try different keywords or clear the search."
            : "Your inventory is empty. Start building your catalog by adding your first product."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {searchQuery && (
            <button
              onClick={onClearSearch}
              className="inline-flex items-center justify-center px-5 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
            >
              <X className="w-4 h-4 mr-2" /> Clear search
            </button>
          )}
          <button
            onClick={() => navigate("../add-product")}
            className="inline-flex items-center justify-center px-5 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-sm font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" />
            {searchQuery ? "Add new product" : "Add your first product"}
          </button>
        </div>
      </div>
    );
  }

  // --- List ----------------------------------------------------------------
  return (
    <>
      {/* Desktop: borderless table on the page — just header + row dividers */}
      <div className="hidden md:block">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
              <th className="py-3 pr-4 font-semibold">Product</th>
              <th className="py-3 px-4 text-right font-semibold">Price</th>
              <th className="py-3 px-4 text-right font-semibold">Stock</th>
              <th className="py-3 px-4 font-semibold">Status</th>
              <th className="py-3 px-4 font-semibold">Added</th>
              <th className="py-3 pl-4 text-right font-semibold w-px">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => {
              const variantCount = product.hasVariants && product.variants ? product.variants.length : 0;
              return (
                <tr key={product._id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <ProductThumb product={product} />
                      <div className="min-w-0">
                        <button
                          onClick={() => editProduct(product._id)}
                          className="block text-left text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors line-clamp-1"
                          title={product.title}
                        >
                          {product.title}
                        </button>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                          {product.sku && <span className="font-mono">{product.sku}</span>}
                          {variantCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-slate-400">
                              <Layers className="w-3 h-3" /> {variantCount} variants
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">{priceLabel(product)}</div>
                    {simpleDiscount(product) !== null && (
                      <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                        {simpleDiscount(product)}% OFF
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span
                      className={`text-sm font-semibold tabular-nums ${
                        !product.stock ? "text-rose-600" : product.stock < 10 ? "text-amber-600" : "text-slate-900"
                      }`}
                    >
                      {product.stock || 0}
                    </span>
                  </td>
                  <td className="py-3 px-4"><StatusBadge stock={product.stock} /></td>
                  <td className="py-3 px-4 text-sm text-slate-500 whitespace-nowrap">
                    {formatDate((product.createdAt || new Date()).toString())}
                  </td>
                  <td className="py-3 pl-4 text-right"><RowActions id={product._id} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: divided list — no per-item boxes */}
      <div className="md:hidden border-t border-slate-200 divide-y divide-slate-100">
        {products.map((product) => {
          const variantCount = product.hasVariants && product.variants ? product.variants.length : 0;
          return (
            <div key={product._id} className="py-3.5">
              <div className="flex items-start gap-3">
                <ProductThumb product={product} className="w-14 h-14" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <button
                      onClick={() => editProduct(product._id)}
                      className="text-left text-sm font-semibold text-slate-900 leading-tight line-clamp-2"
                    >
                      {product.title}
                    </button>
                    <RowActions id={product._id} />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                    {product.sku && <span className="font-mono truncate">{product.sku}</span>}
                    {variantCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-slate-400 flex-shrink-0">
                        <Layers className="w-3 h-3" /> {variantCount}
                      </span>
                    )}
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-base font-bold text-slate-900">{priceLabel(product)}</span>
                      {simpleDiscount(product) !== null && (
                        <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">{simpleDiscount(product)}% OFF</span>
                      )}
                    </div>
                    <StatusBadge stock={product.stock} />
                  </div>
                  <div className="mt-1.5 text-xs text-slate-500">{product.stock || 0} in stock</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
